import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { REQUEST, RESPONSE } from '../tokens/express.tokens';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { CookieOptions, Response } from 'express';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CookieStorageService {
  constructor(
    @Optional() @Inject(RESPONSE) private response: Response,
    @Optional() @Inject(REQUEST) private request: Response,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  public set(name: string, data: string, expireDate?: Date) {
    const expireDateNumeric = new Date(2033, 0, 1);
    const expireDateUTC = new Date(expireDateNumeric);

    if (isPlatformServer(this.platformId)) {
      this.setServerCookie(name, data, {
        expires: expireDate ? expireDate : expireDateUTC,
        path: '/',
        domain: environment.domainCookie,
        secure: true,
        sameSite: 'Lax',
      });
    } else if (isPlatformBrowser(this.platformId)) {
      document.cookie = `${name}=${data};expires=${expireDateUTC.toUTCString()};path=/;domain=${
        environment.domainCookie
      };secure;SameSite=Lax`;
    }
  }

  private setServerCookie(
    name: string,
    value: string,
    options: {
      expires?: number | Date;
      path?: string;
      domain?: string;
      secure?: boolean;
      sameSite?: 'Lax' | 'None' | 'Strict';
    } = {}
  ): void {
    const expressOptions: CookieOptions = {};

    if (options.expires) {
      if (typeof options.expires === 'number') {
        expressOptions.expires = new Date(
          new Date().getTime() + options.expires * 1000 * 60 * 60 * 24
        );
      } else {
        expressOptions.expires = options.expires;
      }
    }

    if (options.path) {
      expressOptions.path = options.path;
    }

    if (options.domain) {
      expressOptions.domain = options.domain;
    }

    if (options.secure) {
      expressOptions.secure = options.secure;
    }

    if (options.sameSite) {
      expressOptions.sameSite = options.sameSite.toLowerCase() as 'lax' | 'none' | 'strict';
    }

    this.response?.cookie(name, value, expressOptions);
  }

  public remove(name: string) {
    if (isPlatformBrowser(this.platformId)) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${environment.domainCookie};secure;SameSite=Lax`;
    } else {
      this.response?.clearCookie(name, {
        path: '/',
        domain: environment.domainCookie,
        secure: true,
        sameSite: 'lax',
      });
    }
  }

  public get(name: string): string {
    if (isPlatformBrowser(this.platformId)) {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        const [cookieName, cookieValue] = cookie.split('=');
        if (cookieName === name) {
          return cookieValue;
        }
      }
    } else if (isPlatformServer(this.platformId) && this.request) {
      const cookieHeader = this.request.get('cookie');
      if (cookieHeader) {
        const cookies = cookieHeader.split(';');
        for (const cookie of cookies) {
          const [cookieName, cookieValue] = cookie.split('=');
          if (cookieName.trim() === name) {
            return cookieValue;
          }
        }
      }
    }
    return '';
  }

}
